import React from 'react'

import { useI18N } from '../../../libraries/i18n/hook'
import Button from '../form/Button'
import ExternalLink from '../layout/ExternalLink'
import { JobOfferResource } from '../../../api/job-offers/types'
import { Link } from 'react-router-dom'
import Loading from '../layout/Loading'
import { useAnalytics, ANALYTICS_EVENTS } from '../../../libraries/posthog'
import DescriptionText from '../content/DescriptionText'
import ShareButton from '../form/ShareButton'
import InfoText from '../layout/InfoText'
import { CareerPath } from '../../../api/career-paths/types'

type Props = {
  id: number
  description?: string
  requirements?: string
  externalLink?: string
  careerPaths?: CareerPath[]
  sector?: string
  isInternship?: boolean
  duration?: string
  dateLimitInfo?: string
  contactPerson?: string
  resources?: JobOfferResource[]
  companyId?: number
  divisionId?: number
  subDivisionId?: number
  isLoading: boolean
  hasAgreement?: boolean
  isExternal: boolean
  isRecruitingEvent?: boolean
  applied?: boolean
}
const JobOfferDetails: React.FC<Props> = ({
  id, description, requirements, externalLink, careerPaths, sector, isInternship, duration, dateLimitInfo, contactPerson, resources, companyId, divisionId, subDivisionId, isLoading, hasAgreement, isExternal, applied, isRecruitingEvent,
}) => {
  const { t } = useI18N()
  const { trackEvent } = useAnalytics()

  return (
    <div className='m-4 border-t border-solid border-gray-100'>
      <>
        {isLoading && <div className='flex w-full justify-center py-8'><Loading /></div>}
        {!isLoading && (
          <div className='sm:py-4 pb-4 gap-x-6 flex flex-col-reverse sm:flex-row justify-between text-xs sm:text-sm'>
            <div className='sm:w-[70%]'>
              <InfoSection title={t('common.description')} description={description} />
              <InfoSection title={t('common.min.requirements')} description={requirements} />
            </div>
            <div className='w-full sm:max-w-[40%] md:max-w-[24%]'>
              {externalLink && isExternal && (
                <div className='py-2'>
                  <a
                    href={externalLink}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => {
                      trackEvent(ANALYTICS_EVENTS.JOB_OFFER_LINK_CLICKED, { url: externalLink })
                    }}
                  >
                    <Button size='md' className='w-full'>{t('jobs.see.offer')}</Button>
                  </a>
                </div>
              )}
              {!isExternal && <ViewInternalOfferButton id={id} applied={applied || false} />}
              <InfoSection title={t('content.sector')} description={`${careerPaths?.map(career => ` ${career.name} `) ?? ''} ${careerPaths && careerPaths.length > 0 && sector ? ` | ${sector}` : sector ?? ''}`} />
              <InfoSection
                title={t('common.moreInfo')}
                description={
                  <>
                    {isRecruitingEvent && <div>{t('jobs.invitation.recruiting.event')}</div>}
                    {!isRecruitingEvent && <div>{t('jobs.contract.type')}: {isInternship ? `${t('jobs.internship')}${duration ? ` | ${duration}` : ''}` : t('jobs.unlimited')}</div>}
                    <div>{dateLimitInfo && <>{t('jobs.apply.date.limit')}: {dateLimitInfo}</>}</div>
                    <div>{contactPerson && <>{t('jobs.contact.person')}: {contactPerson}</>}</div>
                    <div>{hasAgreement && <>{t('jobs.internship.agreement')}</>}</div>
                  </>
                }
              />
              <InfoSection
                title={t('jobs.process.train')}
                description={
                  resources && resources.length > 0
                  && resources?.map((resource) => (
                    <div key={resource.link} className='py-2 sm:py-1'>
                      <ExternalLink url={resource.link}>{resource.label}</ExternalLink>
                    </div>
                  ))
                }
              />

              {subDivisionId && (
                <InfoSection
                  title={''}
                  description={
                    <Link to={`/companies/${companyId}/divisions/${divisionId}/sub-divisions/${subDivisionId}`}>
                      <Button size='md' outlined className='w-full'>{t('jobs.process.info')}</Button>
                    </Link>
                  }
                />
              )}
            </div>
          </div>
        )}
      </>
      <div className='self-end text-end'><ShareButton link={`${location.origin}/jobs?jobId=${id}`} /></div>
    </div>
  )
}

const ViewInternalOfferButton: React.FC<{ id: number, applied: boolean }> = ({ id, applied }) => {
  const { t } = useI18N()
  const Wrapper = (children: React.ReactNode) => {
    if(applied) {
      return <>{children}</>
    }
    return (
      <div className='bg-gray-300 rounded-md p-4'>
        <div className='mb-4'><InfoText text={t('jobs.info.profile.with.ai')} /></div>
        {children}
      </div>
    )
  }
  return Wrapper(
    <Link to={`/jobs/${id}/application`}>
      <Button size='md' className='w-full'>{applied ? t('jobs.prepare.for.interview') : t('jobs.apply.offer')}</Button>
    </Link>
  )
}

const InfoSection: React.FC<{ title?: string, description: string | React.ReactNode }> = ({ title, description }) => (
  description && (
    <div className='py-2'>
      {title && <strong>{title}</strong>}
      <DescriptionText>{description}</DescriptionText>
    </div>
  )
)

export default JobOfferDetails
